/**
 * SharePoint data -> `curSuggestionList`. McLeod maps EWA Agile rows locally;
 * Turvo consumes backend-produced Graph suggestions.
 */
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { isSharepointExcelWorkbookTabUrl } from 'constants/SharepointTrustedWorkbookUrls';
import { SHAREPOINT_WORKBOOK_CONTRACT_VERSION_V2 } from 'constants/SharepointWorkbookContract';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import useTMSContext from 'hooks/useTMSContext';
import { createLoadBuildingSuggestion } from 'lib/api/createLoadBuildingSuggestion';
import {
  getSharepointWorkbookEwaContext,
  worksheetNameFromEwaContext,
} from 'lib/api/getSharepointWorkbookEwaContext';
import { getCurrentTab } from 'lib/chromeScripting/util';
import { Maybe, Undef } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';
import type { LoadBuildingSuggestions } from 'types/suggestions/LoadBuildingSuggestions';
import captureException from 'utils/captureException';
import { mapWithConcurrency } from 'utils/mapWithConcurrency';

import { useSharepointEwaAgileRowsState } from '../providers/SharepointEwaAgileRowsProvider';
import { useSharepointLoadBuildingProcessing } from '../providers/SharepointLoadBuildingProcessingProvider';
import { useSharepointLoadBuildingGraphState } from '../providers/context';
import type { SharepointAgileRow } from '../types/Sharepoint';
import { processSharepointGraphSuggestion } from '../utils/processSharepointGraphSuggestion';
import {
  createSharepointTmsMatchCache,
  processSharepointRowForSuggestion,
} from '../utils/processSharepointRowForSuggestion';
import {
  type SharepointPersistCache,
  createSharepointPersistCache,
  resetSharepointPersistCacheForScope,
  withSharepointPersistCache,
} from '../utils/sharepointPersistCache';
import {
  clearSharepointCarouselState,
  isSharepointSuggestion,
  resolveSharepointClickedSuggestion,
} from '../utils/sharepointSuggestions';
import { worksheetCacheKey } from '../utils/worksheetCache';
import { shouldShowSharepointCarouselProcessing } from './sharepointCarouselProcessing';

export { isSharepointSuggestion } from '../utils/sharepointSuggestions';
export { shouldShowSharepointCarouselProcessing } from './sharepointCarouselProcessing';

/** Debounce suggestion list rebuild so rapid Graph refetches do not abort in-flight TMS matching. */
const SHAREPOINT_SUGGESTION_SYNC_DEBOUNCE_MS = 450;
const SHAREPOINT_PERSIST_CONCURRENCY = 4;

function agileRowEligibleForLoadBuildingSuggestion(
  row: SharepointAgileRow
): boolean {
  return Boolean(row.bookingNumber?.trim());
}

export type UseSharepointLoadBuildingSyncOptions = {
  /** When false, clears SharePoint-derived suggestions and skips all Chrome/tab sync. */
  enabled: boolean;
};

/**
 * Renders nothing; runs `useSharepointLoadBuildingSync`. Mount inside `TMSProvider` and (for Turvo)
 * inside {@link SharepointWorkbookGraphProvider}. McLeod also requires {@link SharepointEwaAgileRowsProvider}.
 */
export function SharepointLoadBuildingSyncHost(
  props: UseSharepointLoadBuildingSyncOptions
) {
  useSharepointLoadBuildingSync(props);
  return null;
}

export function useSharepointLoadBuildingSync({
  enabled,
}: UseSharepointLoadBuildingSyncOptions): void {
  const { tmsID, tmsName } = useTMSContext();
  const {
    currentState: { tabId, emailId },
    setCurrentState,
  } = useContext(SidebarStateContext);

  const { setSuggestionSyncProcessing } = useSharepointLoadBuildingProcessing();
  const { workbookRowsResponse } = useSharepointLoadBuildingGraphState();
  const { agileRows } = useSharepointEwaAgileRowsState();

  const agileSuggestionRows = useMemo(() => {
    if (!enabled) {
      return [];
    }
    if (tmsName === TMS.McleodEnterprise || tmsName === TMS.Mcleod) {
      return (agileRows ?? []).filter(
        agileRowEligibleForLoadBuildingSuggestion
      );
    }
    return [];
  }, [enabled, tmsName, agileRows]);

  const hasLoadedGraphSuggestions =
    enabled &&
    tmsName === TMS.Turvo &&
    workbookRowsResponse != null &&
    workbookRowsResponse.contractVersion ===
      SHAREPOINT_WORKBOOK_CONTRACT_VERSION_V2;

  const [isSharepointTabFromUrl, setIsSharepointTabFromUrl] = useState(false);
  const sharepointTabUrlRef = useRef('');
  const syncGenerationRef = useRef(0);
  // Pending debounced suggestion sync; cleared when deps change so rapid Graph updates coalesce.
  const syncDebounceRef = useRef<Maybe<ReturnType<typeof setTimeout>>>(null);
  // After the first user-visible suggestion sync completes, background row updates (e.g. poll)
  // rebuild suggestions without showing the Load Building tab spinner.
  const hasCompletedInitialVisibleSuggestionSyncRef = useRef(false);
  // Last Graph worksheet we synced; a change means the user switched Excel tabs.
  const lastSuggestionWorksheetRef = useRef<Undef<string>>(undefined);
  // Bumped when a debounced suggestion sync starts. Only the newest run may settle shared state
  // (worksheet ref, tab spinner) so a slower predecessor cannot report stale results.
  const suggestionSyncRunIdRef = useRef(0);
  // Last successful persist per row, so unchanged rows skip the API on every poll.
  const persistCacheRef = useRef<SharepointPersistCache>(
    createSharepointPersistCache()
  );

  // Successful Graph fetch implies a trusted SharePoint workbook host tab; do not let a
  // later async getCurrentTab (untrusted URL or failure) override that for suggestion sync.
  const isSharepointTab = useMemo(
    () => enabled && (hasLoadedGraphSuggestions || isSharepointTabFromUrl),
    [enabled, hasLoadedGraphSuggestions, isSharepointTabFromUrl]
  );

  useEffect(() => {
    if (!enabled) {
      setIsSharepointTabFromUrl(false);
      return;
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    void getCurrentTab({ preferSidepanelHostTab: true })
      .then((tab) => {
        const url = tab?.url ?? '';
        sharepointTabUrlRef.current = url.trim();
        setIsSharepointTabFromUrl(isSharepointExcelWorkbookTabUrl(url));
      })
      .catch(() => {
        setIsSharepointTabFromUrl(false);
      });
  }, [enabled, tabId, workbookRowsResponse?.worksheetName]);

  const syncSharepointSuggestions = useCallback(async () => {
    if (!enabled) return;

    const gen = ++syncGenerationRef.current;

    if (!isSharepointTab) {
      if (syncGenerationRef.current !== gen) return;
      setCurrentState(clearSharepointCarouselState);
      return;
    }

    const graphSuggestions =
      tmsName === TMS.Turvo &&
      workbookRowsResponse?.contractVersion ===
        SHAREPOINT_WORKBOOK_CONTRACT_VERSION_V2
        ? workbookRowsResponse.suggestions
        : [];
    if (
      !tmsID ||
      (agileSuggestionRows.length === 0 && graphSuggestions.length === 0)
    ) {
      if (syncGenerationRef.current !== gen) return;
      setCurrentState(clearSharepointCarouselState);
      return;
    }

    // Resolve worksheet name
    let worksheet = workbookRowsResponse?.worksheetName?.trim() ?? '';
    if (!worksheet && tabId != null) {
      const ewa = await getSharepointWorkbookEwaContext(tabId);
      worksheet = worksheetNameFromEwaContext(ewa) ?? '';
    }
    const sourceURL = sharepointTabUrlRef.current;
    const emailIdForCard = emailId || 0;
    const tmsMatchCache = createSharepointTmsMatchCache();

    resetSharepointPersistCacheForScope(
      persistCacheRef.current,
      worksheetCacheKey(sourceURL, worksheet)
    );
    const persist = withSharepointPersistCache(
      createLoadBuildingSuggestion,
      persistCacheRef.current
    );

    // Persist backend Graph suggestions directly. Agile remains on its established
    // local row mapping path until that transport is migrated separately.
    let suggestionResults: Array<LoadBuildingSuggestions | null>;
    if (graphSuggestions.length > 0 && workbookRowsResponse) {
      suggestionResults = await mapWithConcurrency(
        graphSuggestions,
        SHAREPOINT_PERSIST_CONCURRENCY,
        async (suggestion): Promise<LoadBuildingSuggestions | null> => {
          if (syncGenerationRef.current !== gen) {
            return null;
          }
          try {
            return await processSharepointGraphSuggestion({
              suggestion,
              templateId: workbookRowsResponse.templateId,
              sourceURL,
              emailIdForCard,
              persist,
            });
          } catch (err) {
            captureException(err, {
              phase: 'sharepoint-graph-suggestion',
            });
            return null;
          }
        }
      );
    } else {
      suggestionResults = await mapWithConcurrency(
        agileSuggestionRows,
        SHAREPOINT_PERSIST_CONCURRENCY,
        async (row): Promise<LoadBuildingSuggestions | null> => {
          if (syncGenerationRef.current !== gen) {
            return null;
          }
          try {
            return await processSharepointRowForSuggestion({
              row,
              worksheet,
              sourceURL,
              emailIdForCard,
              tmsID,
              tmsMatchCache,
              persist,
            });
          } catch (err) {
            captureException(err, {
              phase: 'sharepoint-agile-row-for-suggestion',
            });
            return null;
          }
        }
      );
    }
    const suggestions = suggestionResults.filter(
      (suggestion): suggestion is LoadBuildingSuggestions => suggestion != null
    );

    if (syncGenerationRef.current !== gen) {
      return;
    }

    setCurrentState((prev) => {
      const withoutSharepoint = prev.curSuggestionList.filter(
        (s) => !isSharepointSuggestion(s)
      );

      const prevSharepoint = prev.curSuggestionList.filter(
        isSharepointSuggestion
      );

      const resolvedClicked = resolveSharepointClickedSuggestion(
        prev.clickedSuggestion,
        suggestions,
        prevSharepoint
      );

      return {
        ...prev,
        curSuggestionList: [...suggestions, ...withoutSharepoint],
        clickedSuggestion: resolvedClicked,
      };
    });
  }, [
    enabled,
    agileSuggestionRows,
    tmsID,
    tmsName,
    emailId,
    tabId,
    setCurrentState,
    isSharepointTab,
    workbookRowsResponse,
  ]);

  useEffect(() => {
    if (enabled) return;
    setCurrentState(clearSharepointCarouselState);
  }, [enabled, setCurrentState]);

  useEffect(() => {
    if (!enabled) {
      setSuggestionSyncProcessing(false);
      hasCompletedInitialVisibleSuggestionSyncRef.current = false;
      lastSuggestionWorksheetRef.current = undefined;
      // Orphan any in-flight run so its finally cannot re-dirty the state we just reset.
      suggestionSyncRunIdRef.current += 1;
      return;
    }

    const worksheet = workbookRowsResponse?.worksheetName?.trim();
    const showProcessing = shouldShowSharepointCarouselProcessing({
      worksheet,
      lastCompletedCarouselWorksheet: lastSuggestionWorksheetRef.current,
      hasCompletedInitialVisibleCarouselSync:
        hasCompletedInitialVisibleSuggestionSyncRef.current,
    });
    // Do not advance lastSuggestionWorksheetRef until the debounced sync finishes.
    // Updating it here raced with syncSharepointSuggestions identity changes: cleanup
    // cleared the spinner, then the re-run saw no worksheet change and never turned it
    // back on (ENG-7699).
    if (showProcessing) {
      setSuggestionSyncProcessing(true);
      // Orphan any in-flight sync now — not only when the debounce fires. Otherwise a
      // predecessor whose finally still thinks it is newest clears the spinner during
      // the 450ms window after a worksheet switch.
      suggestionSyncRunIdRef.current += 1;
    }

    if (syncDebounceRef.current != null) {
      clearTimeout(syncDebounceRef.current);
    }
    syncDebounceRef.current = setTimeout(() => {
      syncDebounceRef.current = null;
      const runId = ++suggestionSyncRunIdRef.current;
      void syncSharepointSuggestions().finally(() => {
        // A newer run started while this one was in flight; it owns the settle.
        if (runId !== suggestionSyncRunIdRef.current) {
          return;
        }

        if (worksheet) {
          lastSuggestionWorksheetRef.current = worksheet;
        }
        hasCompletedInitialVisibleSuggestionSyncRef.current = true;
        // Clear unconditionally: this run may be a background rebuild that inherited a
        // spinner from a cancelled worksheet switch, and nothing else would turn it off.
        setSuggestionSyncProcessing(false);
      });
    }, SHAREPOINT_SUGGESTION_SYNC_DEBOUNCE_MS);

    return () => {
      if (syncDebounceRef.current != null) {
        clearTimeout(syncDebounceRef.current);
        syncDebounceRef.current = null;
        // Do not clear suggestion-sync processing here. Cancelling the debounce mid
        // worksheet-switch (e.g. syncSharepointSuggestions identity change from a Graph
        // row update) must leave the spinner up until the successor run's finally.
      }
    };
  }, [
    enabled,
    setSuggestionSyncProcessing,
    syncSharepointSuggestions,
    workbookRowsResponse?.worksheetName,
  ]);
}
